import React, { useEffect, useMemo, useState } from "react";
import { FaCheck } from "react-icons/fa6";
import { PropagateLoader } from "react-spinners";
import { toast } from "react-toastify";
import { getApiCaller } from "../../libs/apiCaller";

const Support = () => {
  const [supportActiveTab, setSupportActiveTab] = useState("Consumer");
  const handleTabClick = (tab) => setSupportActiveTab(tab);
  const [loading, setLoading] = useState(true);
  const [supportLists, setSupportLists] = useState([]);
  const [sending, setSending] = useState({});
  const [selectedSubject, setSelectedSubject] = useState("");

  useEffect(() => {
    handleFetchSupporLists();
  }, []);

  const handleFetchSupporLists = async () => {
    try {
      setLoading(true);
      const response = await getApiCaller("support");
      if (response.data.status) {
        setSupportLists(response.data.response_data);
      }
    } catch (error) {
      toast.error("Failed to fetch images. Please try again.");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // **Filtered data based on tab selection**
  const filteredSupportLists = useMemo(() => {
    return supportLists?.filter(
      (item) =>
        (supportActiveTab === "Consumer"
          ? item.is_consumer
          : !item.is_consumer) &&
        (selectedSubject === "" || item.subject === selectedSubject)
    );
  }, [supportLists, supportActiveTab, selectedSubject]);

  const [messages, setMessages] = useState({});
  const [errors, setErrors] = useState({});

  const handleInputChange = (id, value) => {
    setMessages((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const handleSend = async (id) => {
    if (!messages[id]?.trim()) {
      setErrors((prev) => ({ ...prev, [id]: "Message cannot be empty!" }));
      return;
    }

    try {
      setSending((prev) => ({ ...prev, [id]: true }));
      const sendResponse = await getApiCaller(
        `support_update?help_id=${id}&replay=${messages[id]}`
      );

      if (sendResponse?.data?.status) {
        setMessages((prev) => ({ ...prev, [id]: "" }));
        setErrors((prev) => ({ ...prev, [id]: "" }));
        toast.success(sendResponse?.data?.response_message);
        handleFetchSupporLists();
      } else {
        toast.error(sendResponse?.data?.response_message);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to send message");
    } finally {
      setSending((prev) => ({ ...prev, [id]: false })); // Remove loading state
    }
  };

  return (
    <>
      <div className="px-4 md:px-10 mt-4">
        <div className="border border-1 px-4 py-3 rounded-t-2xl text-xl mt-4 bg-white">
          <div className="flex justify-between items-center">
            <p className="text-[#284184] font-bold">Support</p>
            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto items-center">
              <p className="text-base">Sort by Subject :</p>
              <select
                className="border rounded-md px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => setSelectedSubject(e.target.value)}
              >
                <option value="">All</option>
                <option value="Billing">Billing</option>
                <option value="Booking">Booking</option>
                <option value="Others">Others</option>
              </select>
            </div>
          </div>
        </div>
        <div className="border border-y-0 text-xl bg-white px-2 py-2">
          <div className="flex justify-between bg-[#F4F9FF] w-full p-1 rounded-xl">
            {["Consumer", "Provider"].map((tab) => (
              <button
                key={tab}
                className={`w-full px-4 py-2 text-[#80838D] rounded-lg text-left md:text-center ${
                  supportActiveTab === tab && "bg-[#284184] text-white"
                }`}
                onClick={() => handleTabClick(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="border border-t-0 rounded-b-2xl text-xl bg-white px-2 py-2">
          {loading ? (
            <div className="text-center flex items-center justify-center my-10 h-[45vh]">
              <PropagateLoader color="#284184" loading={true} size={10} />
            </div>
          ) : filteredSupportLists.length === 0 ? (
            <p className="text-center py-4">No support requests found.</p>
          ) : (
            filteredSupportLists.map((item) => (
              <div key={item.id} className="mb-6 border p-4 rounded-xl">
                <div className="flex items-center gap-4">
                  <img
                    src={
                      item.profile_picture ||
                      "https://t4.ftcdn.net/jpg/03/64/21/11/360_F_364211147_1qgLVxv1Tcq0Ohz3FawUfrtONzz8nq3e.jpg"
                    }
                    alt="Profile"
                    className="w-16 h-16 rounded-xl object-cover"
                  />

                  <div className="flex justify-between flex-1 gap-4 text-sm md:text-base">
                    <div className="flex flex-col">
                      <span className="text-[#8B8D98]">Name</span>
                      <span>{item.name}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[#8B8D98]">Mobile Number</span>
                      <span>
                        {item.country_code} {item.number}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[#8B8D98]">Email Address</span>
                      <span>{item.email}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[#8B8D98]">Date</span>
                      <span>{item.date}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-end w-[150px]">
                    <span
                      className={`px-4 py-2 bg-[#E6F8E9] text-[#00A651] rounded-md text-sm flex items-center justify-end gap-1`}
                    >
                      <FaCheck />
                      Resolve
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="py-4">
                    <span className="text-[#8B8D98]">Message</span>
                    <p className="mt-2">{item.message}</p>
                  </div>

                  <div className="border-t pt-4">
                    <span className="font-semibold">
                      Message From Your Side
                    </span>
                    <div className="mt-2 flex flex-col gap-2">
                      <textarea
                        rows={4}
                        placeholder="Write message..."
                        className={`flex-1 border rounded-md p-2 text-sm outline-none focus:ring ${
                          errors[item.id]
                            ? "border-red-500 focus:ring-red-300"
                            : "focus:ring-blue-300"
                        }`}
                        value={messages[item.id] || ""}
                        onChange={(e) =>
                          handleInputChange(item.id, e.target.value)
                        }
                      />
                      {errors[item.id] && (
                        <p className="text-red-500 text-sm">
                          {errors[item.id]}
                        </p>
                      )}
                      <div className="text-right">
                        <button
                          className="bg-[#284184] text-white px-4 py-2 rounded-md text-sm hover:bg-[#1f3269]"
                          onClick={() => handleSend(item?.id)}
                          disabled={sending[item?.id]}
                        >
                          {sending[item?.id] ? "Sending..." : "Send"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Support;
